/**
 * Winning Moment - Lottery Ticket Visualization Tool
 * 
 * A hyper-realistic lottery ticket experience for identity rehearsal.
 * This is NOT gambling - it's a visualization and identity installation tool.
 * 
 * Flow: Select numbers → Reveal → Jackpot match → Emotional anchor
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles, Star, Trophy } from 'lucide-react';

// Generate random unique numbers
const generateNumbers = (count, max) => {
  const numbers = new Set();
  while (numbers.size < count) {
    numbers.add(Math.floor(Math.random() * max) + 1);
  }
  return Array.from(numbers).sort((a, b) => a - b);
};

// Number Ball Component
const NumberBall = ({ number, isRevealed, isWinning, delay, onReveal }) => {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (isRevealed) {
      const timer = setTimeout(() => {
        setRevealed(true);
        if (onReveal) onReveal();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [isRevealed, delay, onReveal]);

  return (
    <motion.div
      className="relative w-14 h-14"
      animate={{
        scale: revealed ? [1, 1.2, 1] : 1,
      }}
      transition={{ duration: 0.3 }}
    >
      {/* Glow effect for revealed winning numbers */}
      {revealed && isWinning && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1.5 }}
          className="absolute inset-0 rounded-full bg-[#CBA153]/30 blur-xl"
        />
      )}

      <motion.div
        className={`
          w-full h-full rounded-full flex items-center justify-center font-mono text-xl font-bold
          ${revealed 
            ? isWinning 
              ? 'bg-gradient-to-b from-[#CBA153] to-[#8B6914] text-[#1A1A18] shadow-[0_0_20px_rgba(203,161,83,0.5)]' 
              : 'bg-[#1A1A18] text-[#F2EFE9] border border-[#2C2C28]'
            : 'bg-[#2C2C28]'
          }
        `}
        animate={{
          rotateY: revealed ? [0, 180, 360] : 0,
        }}
        transition={{ duration: 0.6 }}
      >
        {revealed ? number : '?'}
      </motion.div>
    </motion.div>
  );
};

// Lottery Ticket Visual
const LotteryTicket = ({ numbers, winningNumbers, isRevealing, onRevealComplete }) => {
  const [revealedCount, setRevealedCount] = useState(0);
  const totalNumbers = numbers.length;

  const handleReveal = useCallback(() => {
    setRevealedCount(prev => {
      const next = prev + 1;
      if (next === totalNumbers && onRevealComplete) {
        setTimeout(onRevealComplete, 1000);
      }
      return next;
    });
  }, [totalNumbers, onRevealComplete]);

  return (
    <div className="relative mx-auto max-w-sm">
      {/* Ticket Border Glow */}
      <motion.div
        className="absolute inset-0 rounded-3xl"
        animate={{
          boxShadow: isRevealing 
            ? ['0 0 0 rgba(203,161,83,0)', '0 0 30px rgba(203,161,83,0.3)', '0 0 0 rgba(203,161,83,0)']
            : '0 0 0 rgba(203,161,83,0)'
        }}
        transition={{ duration: 2, repeat: Infinity }}
      />

      {/* Ticket Card */}
      <div className="relative bg-gradient-to-b from-[#1F1F1D] to-[#171715] rounded-3xl p-6 border border-[#2C2C28] overflow-hidden">
        {/* Holographic shimmer */}
        <motion.div
          className="absolute inset-0 opacity-10"
          style={{
            background: 'linear-gradient(45deg, transparent 30%, rgba(203,161,83,0.3) 50%, transparent 70%)',
            backgroundSize: '200% 200%',
          }}
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />

        {/* Header */}
        <div className="relative text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Star size={16} className="text-[#CBA153]" fill="currentColor" />
            <span className="text-[#CBA153] text-xs uppercase tracking-[0.3em] font-semibold">
              National Lottery
            </span>
            <Star size={16} className="text-[#CBA153]" fill="currentColor" />
          </div>
          <h3 className="font-['Playfair_Display'] text-2xl text-[#F2EFE9]">
            Lotto
          </h3>
        </div>

        {/* Numbers Grid */}
        <div className="relative flex justify-center gap-3 mb-6 flex-wrap">
          {numbers.map((num, i) => (
            <NumberBall
              key={i}
              number={num}
              isRevealed={isRevealing}
              isWinning={winningNumbers.includes(num)}
              delay={i * 400}
              onReveal={handleReveal}
            />
          ))}
        </div>

        {/* Bonus Ball */}
        <div className="relative text-center mb-6">
          <p className="text-[#9E9A90] text-xs uppercase tracking-wider mb-2">Bonus Ball</p>
          <div className="flex justify-center">
            <NumberBall
              number={winningNumbers[winningNumbers.length - 1]}
              isRevealed={isRevealing}
              isWinning={true}
              delay={numbers.length * 400 + 500}
              onReveal={handleReveal}
            />
          </div>
        </div>

        {/* Draw Info */}
        <div className="relative text-center border-t border-[#2C2C28] pt-4">
          <p className="text-[#9E9A90] text-xs">
            Draw Date: {new Date().toLocaleDateString('en-GB', { 
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

// Jackpot Celebration Screen
const JackpotCelebration = ({ amount, onContinue }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 flex items-center justify-center bg-[#121210] z-50"
    >
      {/* Particle effects */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full bg-[#CBA153]"
          initial={{ 
            x: '50%',
            y: '50%',
            opacity: 0,
            scale: 0
          }}
          animate={{ 
            x: `${Math.random() * 100}%`,
            y: `${Math.random() * 100}%`,
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0]
          }}
          transition={{ 
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2
          }}
        />
      ))}

      <div className="text-center px-8 max-w-md relative z-10">
        {/* Trophy Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, type: 'spring' }}
          className="w-24 h-24 rounded-full bg-gradient-to-b from-[#CBA153] to-[#8B6914] mx-auto mb-8 flex items-center justify-center shadow-[0_0_40px_rgba(203,161,83,0.4)]"
        >
          <Trophy size={40} className="text-[#1A1A18]" />
        </motion.div>

        {/* Amount */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-[#CBA153] text-xs uppercase tracking-[0.3em] mb-2">
            Jackpot Winner
          </p>
          <h1 className="font-['Playfair_Display'] text-5xl text-[#F2EFE9] mb-8">
            £{amount.toLocaleString()}
          </h1>
        </motion.div>

        {/* Affirmation */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="font-['Playfair_Display'] text-2xl text-[#F2EFE9] mb-12 italic"
        >
          "You are the one who receives"
        </motion.p>

        {/* Continue Button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          onClick={onContinue}
          className="px-8 py-4 rounded-2xl bg-[#1A1A18] border border-[#2C2C28] hover:border-[#CBA153]/40 text-[#F2EFE9] transition-all duration-500"
        >
          Carry this feeling with you
        </motion.button>
      </div>
    </motion.div>
  );
};

// Main Component
export const WinningMoment = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('intro'); // intro, select, revealing, celebration
  const [userNumbers, setUserNumbers] = useState([]);
  const [winningNumbers, setWinningNumbers] = useState([]);
  const [jackpotAmount, setJackpotAmount] = useState(0);

  // Generate jackpot amount
  useEffect(() => {
    const amounts = [5000000, 10000000, 15000000, 20000000, 25000000];
    setJackpotAmount(amounts[Math.floor(Math.random() * amounts.length)]);
  }, []);

  // Auto-generate or user select numbers
  const generateUserNumbers = () => {
    const nums = generateNumbers(6, 59);
    setUserNumbers(nums);
    setWinningNumbers(nums); // Same as user's numbers - they always win!
    setPhase('select');
  };

  const startReveal = () => {
    setPhase('revealing');
  };

  const handleRevealComplete = () => {
    setTimeout(() => {
      setPhase('celebration');
    }, 1500);
  };

  const handleComplete = () => {
    navigate('/abundance');
  };

  return (
    <div className="min-h-screen bg-[#121210]">
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#CBA153]/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 pt-10 pb-6">
        <button
          onClick={() => navigate('/abundance')}
          className="p-2 rounded-lg hover:bg-[#1A1A18] transition-colors"
        >
          <ArrowLeft size={24} className="text-[#9E9A90]" />
        </button>
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#CBA153]" />
          <span className="text-[#CBA153] text-xs uppercase tracking-[0.2em]">
            Winning Moment
          </span>
        </div>
        <div className="w-10" /> {/* Spacer for centering */}
      </header>

      {/* Content */}
      <main className="relative z-10 px-6">
        <AnimatePresence mode="wait">
          {/* Intro Phase */}
          {phase === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-12"
            >
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[#9E9A90] text-xs uppercase tracking-[0.25em] mb-4"
              >
                Visualization Experience
              </motion.p>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="font-['Playfair_Display'] text-4xl text-[#F2EFE9] mb-6"
              >
                Your Winning Moment
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-[#9E9A90] text-lg mb-12 max-w-xs mx-auto"
              >
                Feel what it's like to be the one who receives
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <button
                  onClick={generateUserNumbers}
                  className="w-full max-w-xs py-5 rounded-2xl bg-gradient-to-b from-[#CBA153] to-[#8B6914] text-[#1A1A18] font-semibold text-lg shadow-[0_0_30px_rgba(203,161,83,0.3)] hover:shadow-[0_0_40px_rgba(203,161,83,0.5)] transition-all duration-500"
                  data-testid="generate-numbers-btn"
                >
                  Generate Lucky Numbers
                </button>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-[#9E9A90]/50 text-xs mt-8"
              >
                This is a visualization tool, not real gambling
              </motion.p>
            </motion.div>
          )}

          {/* Select Phase - Show ticket with numbers */}
          {phase === 'select' && (
            <motion.div
              key="select"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-8"
            >
              <div className="text-center mb-8">
                <p className="text-[#9E9A90] text-xs uppercase tracking-[0.2em] mb-2">
                  Tonight's Jackpot
                </p>
                <h2 className="font-['Playfair_Display'] text-3xl text-[#CBA153]">
                  £{jackpotAmount.toLocaleString()}
                </h2>
              </div>

              <LotteryTicket 
                numbers={userNumbers}
                winningNumbers={winningNumbers}
                isRevealing={false}
              />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 text-center"
              >
                <button
                  onClick={startReveal}
                  className="w-full max-w-xs py-5 rounded-2xl bg-[#1A1A18] border border-[#CBA153]/30 hover:border-[#CBA153]/60 text-[#F2EFE9] font-semibold text-lg transition-all duration-500"
                  data-testid="reveal-result-btn"
                >
                  Reveal Result
                </button>
              </motion.div>
            </motion.div>
          )}

          {/* Revealing Phase */}
          {phase === 'revealing' && (
            <motion.div
              key="revealing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-8"
            >
              <div className="text-center mb-8">
                <p className="text-[#CBA153] text-xs uppercase tracking-[0.2em] animate-pulse">
                  Drawing Numbers...
                </p>
              </div>

              <LotteryTicket 
                numbers={userNumbers}
                winningNumbers={winningNumbers}
                isRevealing={true}
                onRevealComplete={handleRevealComplete}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Celebration Overlay */}
        {phase === 'celebration' && (
          <JackpotCelebration 
            amount={jackpotAmount}
            onContinue={handleComplete}
          />
        )}
      </main>
    </div>
  );
};

export default WinningMoment;
